import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { SensorType } from '@grosync/common';
import type { DeviceInfo, FarmInfo, SensorReading } from '@grosync/common';
import {
  AdminTopBar,
  ViewScroll,
  Sym,
  IconButton,
  StatusPill,
  ModelTag,
  Metric,
  SectionLabel,
  InfoRow,
  type DeviceStatus,
} from '../../ui';
import { ContentToolbar } from '../../ui/ContentToolbar';
import { LoadingSpinner, PageError } from '../../components/LoadingSpinner';
import { fetchDevices, updateDevice } from '../../api/devices';
import DeviceControlPanel from './components/DeviceControlPanel';
import { useDeviceModels } from '../device-models/useDeviceModels';
import { fetchFarms } from '../../api/farms';
import { AddDeviceModal } from './AddDeviceModal';

/* ============================================================================
 * 장비 — 밀집 테이블 + 상세 드로어 (Claude Design 재구현)
 * ========================================================================= */

/** 백엔드 DeviceStatus → 디자인 ok/warn/off */
function toUiStatus(s: DeviceInfo['status']): DeviceStatus {
  switch (s) {
    case 'ACTIVE':
      return 'ok';
    case 'OFFLINE':
      return 'off';
    default:
      return 'warn'; // WARNING | ERROR
  }
}

type FilterKey = 'all' | 'ok' | 'warn' | 'off' | 'm1' | 'm2';

const SENSOR_TONE = {
  temp: '#E07B3E',
  humidity: '#2069C7',
  co2: '#178A50',
  illuminance: '#C77B20',
} as const;

/** latestReadings에서 센서 타입별 최신값 추출 */
function readingOf(readings: SensorReading[] | undefined, type: SensorType): number | null {
  const r = readings?.find((x) => x.sensorType === type);
  return r ? r.value : null;
}

function fmtNum(v: number | null, digits = 0): string {
  return v == null ? '–' : v.toFixed(digits);
}

/** 마지막 수신 시각 → 상대 표기 */
function fmtRelative(iso: string | null): string {
  if (!iso) return '–';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '–';
  const min = Math.floor(diff / 60_000);
  if (min < 1) return '방금 전';
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  return `${day}일 전`;
}

const DEVICE_TYPE_LABEL: Record<string, string> = {
  sensor_node: '센서 노드',
  gateway: '게이트웨이',
  actuator: '액추에이터',
};

const COLS = '1.7fr 1.3fr 1fr 0.9fr 2.4fr 0.8fr 0.9fr';

export default function DevicesView(): React.ReactElement {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sel, setSel] = useState<DeviceInfo | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const devicesQuery = useQuery({ queryKey: ['devices'], queryFn: fetchDevices });
  const farmsQuery = useQuery({ queryKey: ['farms'], queryFn: fetchFarms });

  const devices = devicesQuery.data ?? [];
  const farms = farmsQuery.data ?? [];

  const farmById = useMemo(() => new Map<string, FarmInfo>(farms.map((f) => [f.id, f])), [farms]);

  const counts = useMemo(() => {
    const ui = devices.map((d) => toUiStatus(d.status));
    return {
      all: devices.length,
      ok: ui.filter((s) => s === 'ok').length,
      warn: ui.filter((s) => s === 'warn').length,
      off: ui.filter((s) => s === 'off').length,
      m1: devices.filter((d) => (d.model ?? 1) === 1).length,
      m2: devices.filter((d) => d.model === 2).length,
    };
  }, [devices]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return devices.filter((d) => {
      const ui = toUiStatus(d.status);
      const okF =
        filter === 'all'
          ? true
          : filter === 'm1'
            ? (d.model ?? 1) === 1
            : filter === 'm2'
              ? d.model === 2
              : ui === filter;
      const farm = d.farmId ? farmById.get(d.farmId) : undefined;
      const okQ =
        !needle ||
        [d.id, d.name, farm?.name, farm?.location?.description, d.cropType].some((s) =>
          (s ?? '').toLowerCase().includes(needle),
        );
      return okF && okQ;
    });
  }, [devices, filter, q, farmById]);

  const filters: Array<{ key: FilterKey; label: string; n: number }> = [
    { key: 'all', label: '전체', n: counts.all },
    { key: 'ok', label: '정상', n: counts.ok },
    { key: 'warn', label: '경고', n: counts.warn },
    { key: 'off', label: '오프라인', n: counts.off },
    { key: 'm1', label: '환경', n: counts.m1 },
    { key: 'm2', label: '환경 · 생육', n: counts.m2 },
  ];

  const handleExportCsv = (): void => {
    const header = ['장비 ID', '장비명', '농장', '위치', '모델', '상태', '온도', '습도', 'CO₂', '조도', '펌웨어', '최근수신'];
    const lines = devices.map((d) => {
      const farm = d.farmId ? farmById.get(d.farmId) : undefined;
      const cells = [
        d.id,
        d.name,
        farm?.name ?? '',
        farm?.location?.description ?? '',
        d.model === 2 ? '환경·생육' : '환경',
        d.status,
        fmtNum(readingOf(d.latestReadings, SensorType.TEMPERATURE), 1),
        fmtNum(readingOf(d.latestReadings, SensorType.HUMIDITY)),
        fmtNum(readingOf(d.latestReadings, SensorType.CO2)),
        fmtNum(readingOf(d.latestReadings, SensorType.ILLUMINANCE)),
        d.firmwareVersion,
        d.lastSeenAt ?? '',
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
    });
    const csv = `﻿${[header.join(','), ...lines].join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `devices_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isLoading = devicesQuery.isLoading || farmsQuery.isLoading;
  const error = devicesQuery.error ?? farmsQuery.error;

  return (
    <>
      <AdminTopBar
        title="장비"
        subtitle={`전체 ${counts.all}대 · 정상 ${counts.ok} · 경고 ${counts.warn} · 오프라인 ${counts.off}`}
      />

      <ViewScroll gap={18}>
        <ContentToolbar
          search={q}
          onSearch={setQ}
          searchPlaceholder="장비 ID · 이름 · 농장 검색"
          actions={
            <>
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                style={{
                  height: 42,
                  padding: '0 16px',
                  borderRadius: 9999,
                  cursor: 'pointer',
                  background: 'var(--md-sys-color-primary)',
                  border: 0,
                  color: 'var(--md-sys-color-on-primary)',
                  font: '600 14px/1 var(--gs-font-kr)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Sym name="add" size={18} />
                장비 추가
              </button>
              <button
                type="button"
                onClick={handleExportCsv}
                style={{
                  height: 42,
                  padding: '0 16px',
                  borderRadius: 9999,
                  cursor: 'pointer',
                  background: 'transparent',
                  border: '1px solid var(--md-sys-color-outline)',
                  color: 'var(--md-sys-color-on-surface)',
                  font: '600 14px/1 var(--gs-font-kr)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Sym name="download" size={18} />
                CSV 내보내기
              </button>
            </>
          }
          filters={filters.map((ff) => {
            const active = filter === ff.key;
            return (
              <button
                key={ff.key}
                type="button"
                onClick={() => setFilter(ff.key)}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 9999,
                  cursor: 'pointer',
                  border: active ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                  background: active ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                  color: active
                    ? 'var(--md-sys-color-on-secondary-container)'
                    : 'var(--md-sys-color-on-surface-variant)',
                  font: `${active ? 600 : 500} 13px/1 var(--gs-font-kr)`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {ff.label}
                <span className="gs-num" style={{ fontWeight: 700, opacity: active ? 1 : 0.7 }}>
                  {ff.n}
                </span>
              </button>
            );
          })}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <PageError message={(error as Error).message} />
        ) : (
          <>
            {/* 테이블 */}
            <div
              style={{
                background: 'var(--md-sys-color-surface-container-lowest)',
                border: '1px solid var(--md-sys-color-outline-variant)',
                borderRadius: 18,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  gap: 14,
                  padding: '13px 22px',
                  borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                  background: 'var(--md-sys-color-surface-container-low)',
                }}
              >
                {['장비', '농장 · 위치', '모델', '상태', '센서 (온·습·CO₂·조도)', '배터리', '수신'].map((h) => (
                  <span
                    key={h}
                    style={{
                      font: '600 12px/16px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {rows.map((d, i) => {
                const ui = toUiStatus(d.status);
                const off = ui === 'off';
                const farm = d.farmId ? farmById.get(d.farmId) : undefined;
                const temp = readingOf(d.latestReadings, SensorType.TEMPERATURE);
                const hum = readingOf(d.latestReadings, SensorType.HUMIDITY);
                const co2 = readingOf(d.latestReadings, SensorType.CO2);
                const lux = readingOf(d.latestReadings, SensorType.ILLUMINANCE);
                return (
                  <div
                    key={d.id}
                    className="gs-row"
                    onClick={() => setSel(d)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: COLS,
                      gap: 14,
                      padding: '13px 22px',
                      alignItems: 'center',
                      cursor: 'pointer',
                      background:
                        sel?.id === d.id ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                      borderBottom:
                        i === rows.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                    }}
                  >
                    {/* 장비 */}
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          font: '600 13px/17px var(--gs-font-kr)',
                          color: 'var(--md-sys-color-on-surface)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {d.name}
                      </div>
                      <div
                        className="gs-num"
                        style={{
                          font: '400 11px/14px var(--gs-font-kr)',
                          color: 'var(--md-sys-color-on-surface-variant)',
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {d.id}
                      </div>
                    </div>

                    {/* 농장 · 위치 */}
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          font: '500 13px/16px var(--gs-font-kr)',
                          color: 'var(--md-sys-color-on-surface)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {farm?.name ?? '미할당'}
                      </div>
                      <div
                        style={{
                          font: '400 11px/14px var(--gs-font-kr)',
                          color: 'var(--md-sys-color-on-surface-variant)',
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {farm?.location?.description ?? d.location?.description ?? '–'}
                      </div>
                    </div>

                    {/* 모델 */}
                    <div>
                      <ModelTag m={d.model ?? 1} />
                    </div>

                    {/* 상태 */}
                    <div>
                      <StatusPill s={ui} />
                    </div>

                    {/* 센서 */}
                    <div style={{ minWidth: 0 }}>
                      {!off ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                          <SensorMini icon="thermostat" v={fmtNum(temp, 1)} u="°C" tone={SENSOR_TONE.temp} warn={temp != null && temp > 30} />
                          <SensorMini icon="water_drop" v={fmtNum(hum)} u="%" tone={SENSOR_TONE.humidity} warn={hum != null && hum < 50} />
                          <SensorMini icon="co2" v={fmtNum(co2)} u="ppm" tone={SENSOR_TONE.co2} warn={co2 != null && co2 > 700} />
                          <SensorMini icon="light_mode" v={lux == null ? '–' : (lux / 1000).toFixed(1)} u="klx" tone={SENSOR_TONE.illuminance} />
                        </div>
                      ) : (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            font: '400 12px/16px var(--gs-font-kr)',
                            color: 'var(--md-sys-color-on-surface-variant)',
                          }}
                        >
                          <Sym name="signal_disconnected" size={14} />
                          통신 끊김
                        </span>
                      )}
                    </div>

                    {/* 배터리 — DeviceInfo에 배터리 필드 없음 */}
                    <span
                      style={{
                        font: '400 12px/16px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                      }}
                    >
                      –
                    </span>

                    {/* 수신 */}
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                      <span
                        style={{
                          font: '400 12px/16px var(--gs-font-kr)',
                          color: 'var(--md-sys-color-on-surface-variant)',
                        }}
                      >
                        {fmtRelative(d.lastSeenAt)}
                      </span>
                      <Sym
                        name="chevron_right"
                        size={18}
                        style={{ color: 'var(--md-sys-color-on-surface-variant)', flexShrink: 0 }}
                      />
                    </span>
                  </div>
                );
              })}

              {!rows.length && (
                <div
                  style={{
                    padding: 48,
                    textAlign: 'center',
                    font: '400 14px/20px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                  }}
                >
                  조건에 맞는 장비가 없습니다
                </div>
              )}
            </div>
          </>
        )}
      </ViewScroll>

      {sel && (
        <DeviceDrawer
          key={sel.id}
          device={sel}
          farm={sel.farmId ? farmById.get(sel.farmId) : undefined}
          onClose={() => setSel(null)}
          onViewData={() => {
            navigate(`/data?deviceId=${encodeURIComponent(sel.id)}`);
          }}
        />
      )}

      {addOpen && (
        <AddDeviceModal
          onClose={() => setAddOpen(false)}
          onCreated={(device) => {
            setAddOpen(false);
            setSel(device);
          }}
        />
      )}
    </>
  );
}

/* ---- 테이블 센서 미니 셀 ----------------------------------------------- */
function SensorMini({
  icon,
  v,
  u,
  tone,
  warn,
}: {
  icon: string;
  v: React.ReactNode;
  u: string;
  tone: string;
  warn?: boolean;
}): React.ReactElement {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        minWidth: 0,
        color: warn ? 'var(--gs-warning)' : 'var(--md-sys-color-on-surface-variant)',
      }}
    >
      <Sym name={icon} size={14} style={{ color: warn ? 'var(--gs-warning)' : tone }} />
      <span
        className="gs-num"
        style={{
          font: `${warn ? 700 : 600} 13px/16px var(--gs-font-kr)`,
          color: warn ? 'var(--gs-warning)' : 'var(--md-sys-color-on-surface)',
        }}
      >
        {v}
      </span>
      <span style={{ font: '400 10px/16px var(--gs-font-kr)' }}>{u}</span>
    </span>
  );
}

/* ---- 장비 상세 드로어 -------------------------------------------------- */
/**
 * 장비 모델(code) 인라인 선택 — 레거시(미지정) 장비의 device_model_id를 채운다.
 * 선택 변경 시 PATCH /api/devices/:id 로 deviceModelId(=code)를 저장한다(spec 3.5).
 */
function ModelSelectRow({ device }: { device: DeviceInfo }): React.ReactElement {
  const qc = useQueryClient();
  const { models } = useDeviceModels();
  const mutation = useMutation({
    mutationFn: (code: string) => updateDevice(device.id, { deviceModelId: code }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['devices'] });
      void qc.invalidateQueries({ queryKey: ['device', device.id] });
    },
  });

  const current = device.deviceModelId ?? '';
  // 현재 참조 code가 모델 목록에 없을 수도 있으므로(레거시), 옵션을 보강한다.
  const hasCurrent = current === '' || models.some((m) => m.code === current);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
      <Sym name="memory" size={18} style={{ color: 'var(--md-sys-color-on-surface-variant)' }} />
      <span style={{ width: 72, font: '400 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
        모델
      </span>
      <select
        value={current}
        disabled={mutation.isPending}
        onChange={(e) => mutation.mutate(e.target.value)}
        style={{
          flex: 1,
          height: 34,
          padding: '0 10px',
          borderRadius: 9,
          textAlign: 'right',
          border: '1px solid var(--md-sys-color-outline-variant)',
          background: 'var(--md-sys-color-surface)',
          color: 'var(--md-sys-color-on-surface)',
          font: '500 13px/1 var(--gs-font-kr)',
          cursor: mutation.isPending ? 'default' : 'pointer',
        }}
      >
        <option value="" disabled>
          미지정
        </option>
        {!hasCurrent && <option value={current}>{current} (미등록)</option>}
        {models.map((m) => (
          <option key={m.code} value={m.code}>
            {m.name} ({m.code})
          </option>
        ))}
      </select>
    </div>
  );
}

function DeviceDrawer({
  device,
  farm,
  onClose,
  onViewData,
}: {
  device: DeviceInfo;
  farm: FarmInfo | undefined;
  onClose: () => void;
  onViewData: () => void;
}): React.ReactElement {
  const ui = toUiStatus(device.status);
  const off = ui === 'off';
  const [revealKey, setRevealKey] = useState(false);
  const copy = (text: string): void => {
    void navigator.clipboard.writeText(text);
  };
  const temp = readingOf(device.latestReadings, SensorType.TEMPERATURE);
  const hum = readingOf(device.latestReadings, SensorType.HUMIDITY);
  const co2 = readingOf(device.latestReadings, SensorType.CO2);
  const lux = readingOf(device.latestReadings, SensorType.ILLUMINANCE);
  const received = fmtRelative(device.lastSeenAt);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 20 }}
      />
      <aside
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 430,
          zIndex: 21,
          background: 'var(--md-sys-color-surface)',
          borderLeft: '1px solid var(--md-sys-color-outline-variant)',
          boxShadow: '-12px 0 40px -12px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'drawerIn 240ms cubic-bezier(.2,0,0,1)',
        }}
      >
        <div
          style={{
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            borderBottom: '1px solid var(--md-sys-color-outline-variant)',
          }}
        >
          <span
            style={{
              font: '700 16px/22px var(--gs-font-kr)',
              color: 'var(--md-sys-color-on-surface)',
              flex: 1,
            }}
          >
            장비 상세
          </span>
          <IconButton icon="close" size={22} onClick={onClose} />
        </div>

        <div
          className="gs-scroll-hide"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '22px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {/* identity */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 15,
                flexShrink: 0,
                position: 'relative',
                background: 'var(--md-sys-color-primary-container)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sym
                name={device.model === 2 ? 'eco' : 'sensors'}
                size={28}
                fill={1}
                style={{ color: 'var(--md-sys-color-on-primary-container)' }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: -3,
                  right: -3,
                  width: 14,
                  height: 14,
                  borderRadius: 9999,
                  background:
                    ui === 'ok'
                      ? 'var(--gs-primary-50)'
                      : ui === 'warn'
                        ? 'var(--gs-warning)'
                        : '#9AA095',
                  border: '2.5px solid var(--md-sys-color-surface)',
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  font: '700 19px/24px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface)',
                }}
              >
                {device.name}
              </div>
              <div
                className="gs-num"
                style={{
                  font: '400 13px/18px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                  marginTop: 3,
                  wordBreak: 'break-all',
                }}
              >
                {device.id}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <StatusPill s={ui} />
                <ModelTag m={device.model ?? 1} />
              </div>
            </div>
          </div>

          {/* farm / location */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 14,
              background: 'var(--md-sys-color-surface-container-low)',
              border: '1px solid var(--md-sys-color-outline-variant)',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                flexShrink: 0,
                background: 'var(--md-sys-color-primary-container)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sym
                name="agriculture"
                size={21}
                fill={1}
                style={{ color: 'var(--md-sys-color-on-primary-container)' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  font: '600 14px/18px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface)',
                }}
              >
                {farm?.name ?? '미할당'}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  font: '400 12px/16px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                  marginTop: 2,
                }}
              >
                <Sym name="location_on" size={13} />
                {farm?.location?.description ?? device.location?.description ?? '위치 미지정'}
              </div>
            </div>
          </div>

          {/* sensor readings (2x2) */}
          <div>
            <SectionLabel style={{ marginBottom: 10 }}>실시간 센서</SectionLabel>
            {off ? (
              <div
                style={{
                  padding: 20,
                  borderRadius: 14,
                  background: 'var(--md-sys-color-surface-container)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  color: 'var(--md-sys-color-on-surface-variant)',
                }}
              >
                <Sym name="signal_disconnected" size={20} />
                <span style={{ font: '500 13px/18px var(--gs-font-kr)' }}>
                  통신 끊김 · 마지막 수신 {received}
                </span>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Metric icon="thermostat" label="온도" v={fmtNum(temp, 1)} u="°C" tone={SENSOR_TONE.temp} warn={temp != null && temp > 30} />
                <Metric icon="water_drop" label="습도" v={fmtNum(hum)} u="%" tone={SENSOR_TONE.humidity} warn={hum != null && hum < 50} />
                <Metric icon="co2" label="CO₂" v={fmtNum(co2)} u="ppm" tone={SENSOR_TONE.co2} warn={co2 != null && co2 > 700} />
                <Metric icon="light_mode" label="조도" v={lux == null ? '–' : (lux / 1000).toFixed(1)} u="klx" tone={SENSOR_TONE.illuminance} />
              </div>
            )}
          </div>

          {/* device info */}
          <div>
            <SectionLabel style={{ marginBottom: 4 }}>장비 정보</SectionLabel>
            <ModelSelectRow device={device} />
            <InfoRow icon="category" label="유형" value={DEVICE_TYPE_LABEL[device.type] ?? device.type} />
            <InfoRow icon="system_update" label="펌웨어" value={device.firmwareVersion} />
            <InfoRow icon="lan" label="통신" value="LTE-M / Wi-Fi" />
            <InfoRow icon="battery_full" label="배터리" value="–" />
            <InfoRow icon="schedule" label="최근 수신" value={received} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderTop: '1px solid var(--md-sys-color-outline-variant)' }}>
              <Sym name="tag" size={18} style={{ color: 'var(--md-sys-color-on-surface-variant)' }} />
              <span style={{ flex: 1, font: '500 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>장비 ID</span>
              <span className="gs-num" style={{ font: '500 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', wordBreak: 'break-all' }}>{device.id}</span>
              <IconButton icon="content_copy" size={18} onClick={() => copy(device.id)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderTop: '1px solid var(--md-sys-color-outline-variant)' }}>
              <Sym name="key" size={18} style={{ color: 'var(--md-sys-color-on-surface-variant)' }} />
              <span style={{ flex: 1, font: '500 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>API Key</span>
              <span className="gs-num" style={{ font: '500 12px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {device.mqttApiKey ? (revealKey ? device.mqttApiKey : '••••••••') : '—'}
              </span>
              {device.mqttApiKey && (
                <IconButton icon={revealKey ? 'visibility_off' : 'visibility'} size={18} onClick={() => setRevealKey((v) => !v)} />
              )}
              {device.mqttApiKey && <IconButton icon="content_copy" size={18} onClick={() => copy(device.mqttApiKey!)} />}
            </div>
          </div>

          <div style={{ padding: '18px 20px', borderTop: '1px solid var(--md-sys-color-outline-variant)' }}>
            <DeviceControlPanel deviceId={device.id} deviceModelId={device.deviceModelId} model={device.model} />
          </div>
        </div>

        {/* footer actions */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--md-sys-color-outline-variant)',
            display: 'flex',
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              height: 46,
              borderRadius: 12,
              cursor: 'pointer',
              background: 'transparent',
              border: '1px solid var(--md-sys-color-outline)',
              color: 'var(--md-sys-color-on-surface)',
              font: '600 14px/1 var(--gs-font-kr)',
            }}
          >
            닫기
          </button>
          <button
            type="button"
            onClick={onViewData}
            style={{
              flex: 1.4,
              height: 46,
              borderRadius: 12,
              border: 0,
              cursor: 'pointer',
              background: 'var(--md-sys-color-primary)',
              color: 'var(--md-sys-color-on-primary)',
              font: '600 14px/1 var(--gs-font-kr)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Sym name="analytics" size={18} />
            데이터 조회
          </button>
        </div>
      </aside>
    </>
  );
}
